import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type OriginalCaseStatus = 'NEW' | 'COMPLETED' | 'REJECTED'
export type OriginalCaseStatusFilter = OriginalCaseStatus | 'ALL'

export type OriginalCaseStudio = {
  studio_internal_id: number
  studio_number: number | null
  city_name: string | null
  country: string | null
  address: string | null
  status_code: string | null
  owner_partner_number: number | null
  owner_persons_fio: string | null
  source_row: Record<string, unknown>
}

export type OriginalCaseStudioCandidate = Omit<OriginalCaseStudio, 'source_row'>

export type OriginalCaseCabinetRow = {
  id: number
  event_type: string
  event_label: string
  target_status: string | null
  changed_at: string
  status: OriginalCaseStatus
  object_type: string
  object_id: string | null
  object_display_name: string | null
  current_data: Record<string, unknown>
  payload: Record<string, unknown>
  comment: string | null
  attachments: Array<Record<string, unknown>>
  studio_count: number
  is_bulk: boolean
  created_by_user_id: string
  created_by_name: string | null
  created_at: string
  read_at: string | null
  completed_at: string | null
  completed_by_name: string | null
  rejected_at: string | null
  rejected_by_name: string | null
  rejection_reason: string | null
  rejection_comment: string | null
  studios: OriginalCaseStudio[]
}

export type OriginalCaseNotification = {
  id: number
  event_type: string
  event_label: string
  object_display_name: string | null
  created_at: string
  created_by_name: string | null
}

export type OriginalCaseNotifications = {
  unread_count: number
  items: OriginalCaseNotification[]
}

type RpcError = { message?: string } | null

type OriginalCaseCabinetRpcClient = {
  rpc: (name: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: RpcError }>
}

function client() {
  return getSupabase() as unknown as OriginalCaseCabinetRpcClient
}

function throwIfError(error: RpcError) {
  if (error) throw error
}

export async function listOriginalChangeCases(status: OriginalCaseStatusFilter): Promise<OriginalCaseCabinetRow[]> {
  const { data, error } = await withAuthRecovery(() =>
    client().rpc('list_original_change_cases', { p_status: status })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
  return Array.isArray(data) ? data as OriginalCaseCabinetRow[] : []
}

export async function getOriginalCaseNotifications(): Promise<OriginalCaseNotifications> {
  const { data, error } = await withAuthRecovery(() =>
    client().rpc('get_original_change_case_notifications', { p_limit: 5 })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
  const payload = data && typeof data === 'object' ? data as Partial<OriginalCaseNotifications> : {}
  return {
    unread_count: Number(payload.unread_count ?? 0),
    items: Array.isArray(payload.items) ? payload.items as OriginalCaseNotification[] : [],
  }
}

export async function markOriginalChangeCaseRead(caseId: number): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    client().rpc('mark_original_change_case_read', { p_case_id: caseId })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
}

export async function completeOriginalChangeCase(caseId: number): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    client().rpc('complete_original_change_case', { p_case_id: caseId })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
}

export async function rejectOriginalChangeCase(params: {
  caseId: number
  reason: string
  comment?: string | null
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    client().rpc('reject_original_change_case', {
      p_case_id: params.caseId,
      p_reason: params.reason,
      p_comment: params.comment ?? null,
    })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
}

export async function listOriginalChangeCaseStudioCandidates(search: string): Promise<OriginalCaseStudioCandidate[]> {
  const { data, error } = await withAuthRecovery(() =>
    client().rpc('list_original_change_case_studio_candidates', {
      p_search: search.trim() || null,
      p_limit: 80,
    })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
  return Array.isArray(data) ? data as OriginalCaseStudioCandidate[] : []
}

export async function updateOriginalChangeCaseScope(params: {
  caseId: number
  changedAt: string
  studioInternalIds: number[]
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    client().rpc('update_original_change_case_scope', {
      p_case_id: params.caseId,
      p_changed_at: params.changedAt,
      p_studio_internal_ids: params.studioInternalIds,
    })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
}

export async function updateOriginalChangeCaseDetails(params: {
  caseId: number
  changedAt: string
  targetStatus: string | null
  studioInternalIds: number[]
  payload: Record<string, unknown>
  comment: string | null
  attachments: Array<Record<string, unknown>>
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    client().rpc('update_original_change_case_details', {
      p_case_id: params.caseId,
      p_changed_at: params.changedAt,
      p_target_status: params.targetStatus,
      p_studio_internal_ids: params.studioInternalIds,
      p_payload: params.payload,
      p_comment: params.comment,
      p_attachments: params.attachments,
    })
  ) as { data: unknown; error: RpcError }

  throwIfError(error)
}
